// Admin Login page (Phase 5c). A trimmer counterpart to the customer Login:
// no SSO providers in v1 (only password + email-token), and a clear
// "Region: {DV|US|EU}" badge so operators know which regional catalog
// they're signing in against.
//
// Done-when (Phase 5c): admin signs in → /api/admin/auth/password/login →
// MFA gate → /api/admin/me → land on /. Mirrors the customer Login test
// shape so the navigation test reuses the same testing-library pattern.

import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import type {
  AdminAuthResponse,
  AdminEmailTokenRequest,
  AdminEmailTokenVerifyRequest,
  AdminPasswordLoginRequest,
} from '@shared/api-types';
import { ApiError, adminApiFetch } from '../../lib/adminApiClient';
import { useAdminAuth } from '../../lib/adminAuth';
import { useRegion } from '../../lib/regionContext';
import { fetchBuildVersion } from '../../lib/buildVersion';
import './Login.css';

type Step = 'email' | 'password' | 'token';

// Local developer sign-in (AAI-115). `import.meta.env.DEV` is a build-time
// constant, so production bundles drop the panel and its requests entirely; in a
// dev build it still renders nothing unless the local API offers the feature.
const DevSignInPanel = import.meta.env.DEV ? lazy(() => import('./DevSignInPanel')) : null;

const MAIL_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAdminAuth();
  const { region } = useRegion();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(
    (location.state as { regionSwitch?: string } | null)?.regionSwitch
      ? `Region switched to ${(location.state as { regionSwitch: string }).regionSwitch.toUpperCase()} — please sign in again.`
      : null,
  );

  const [buildVersion, setBuildVersion] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    void fetchBuildVersion(controller.signal).then((v) => {
      if (!controller.signal.aborted) setBuildVersion(v);
    });
    return () => controller.abort();
  }, []);

  const redirectAfterAuth = useCallback(
    async (response: AdminAuthResponse) => {
      if (response.mfaEnrollmentRequired) {
        await refresh();
        navigate('/mfa-enroll', { replace: true });
        return;
      }
      if (response.requiresMfa) {
        await refresh();
        navigate('/mfa-verify', { replace: true });
        return;
      }
      await refresh();
      navigate('/', { replace: true });
    },
    [navigate, refresh],
  );

  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setFieldError('Please enter a valid email address.');
      return;
    }
    setStep('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    setLoading(true);
    try {
      const body: AdminPasswordLoginRequest = { email, password };
      const response = await adminApiFetch<AdminAuthResponse>('/api/admin/auth/password/login', {
        method: 'POST',
        body,
      });
      if (!response.authenticated) {
        setFieldError('Invalid email or password.');
        return;
      }
      await redirectAfterAuth(response);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFieldError('Invalid email or password.');
      } else {
        setToast('Sign-in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRequestEmailToken = async () => {
    setFieldError(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setFieldError('Enter a valid email address first.');
      return;
    }
    setLoading(true);
    try {
      const body: AdminEmailTokenRequest = { email };
      await adminApiFetch<void>('/api/admin/auth/email-token/request', { method: 'POST', body });
      setStep('token');
      setToast('Sign-in code sent. Check your email.');
    } catch {
      setToast('Could not send sign-in code. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyToken = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldError(null);
    if (!/^\d{6}$/.test(code)) {
      setFieldError('Enter the 6-digit code from the email.');
      return;
    }
    setLoading(true);
    try {
      const body: AdminEmailTokenVerifyRequest = { email, code };
      const response = await adminApiFetch<AdminAuthResponse>('/api/admin/auth/email-token/verify', {
        method: 'POST',
        body,
      });
      if (!response.authenticated) {
        setFieldError('That code is invalid or expired.');
        return;
      }
      await redirectAfterAuth(response);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFieldError('That code is invalid or expired.');
      } else {
        setToast('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <header className="admin-login-head">
          <div className="brand-lockup">
            <img
              className="brand-mark"
              src="/icons/light/automationai-96.png"
              alt=""
              aria-hidden="true"
              width={28}
              height={28}
            />
            <div className="brand-name">Automations — Admin</div>
          </div>
          <div className="region-badge">Region: {region.toUpperCase()}</div>
        </header>

        <main className="admin-login-main">
          {step === 'email' && (
            <form className="step" onSubmit={handleEmailContinue}>
              <h1 className="step-title">Sign in</h1>
              <p className="step-sub">Platform admin sign-in for the Tenant Management app.</p>

              <label className="field">
                <span className="field-label">Work email</span>
                <div className={`field-input ${fieldError ? 'has-error' : ''}`}>
                  <span aria-hidden="true">{MAIL_ICON}</span>
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="you@cloudradial.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setFieldError(null);
                    }}
                  />
                </div>
                {fieldError && <div className="field-msg" role="alert">{fieldError}</div>}
              </label>

              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !email}
                data-testid="email-continue-btn"
              >
                Continue
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={handleRequestEmailToken}
                disabled={loading}
                data-testid="email-token-btn"
              >
                Email me a sign-in code instead
              </button>
            </form>
          )}

          {step === 'email' && DevSignInPanel && (
            <Suspense fallback={null}>
              <DevSignInPanel onSignedIn={redirectAfterAuth} />
            </Suspense>
          )}

          {step === 'password' && (
            <form className="step" onSubmit={handlePasswordSubmit}>
              <h1 className="step-title">Enter your password</h1>
              <p className="step-sub">{email}</p>

              <label className="field">
                <span className="field-label">Password</span>
                <div className={`field-input ${fieldError ? 'has-error' : ''}`}>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFieldError(null);
                    }}
                    placeholder="••••••••"
                  />
                </div>
                {fieldError && <div className="field-msg" role="alert">{fieldError}</div>}
              </label>

              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !password}
                data-testid="password-signin-btn"
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={handleRequestEmailToken}
                disabled={loading}
              >
                Email me a sign-in code instead
              </button>
            </form>
          )}

          {step === 'token' && (
            <form className="step" onSubmit={handleVerifyToken}>
              <h1 className="step-title">Enter your code</h1>
              <p className="step-sub">We sent a 6-digit code to {email}</p>

              <div className={`code-row ${fieldError ? 'has-error' : ''}`}>
                <input
                  className="code-digit-input"
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setFieldError(null);
                  }}
                  aria-label="Six-digit code"
                />
              </div>
              {fieldError && <div className="field-msg" role="alert">{fieldError}</div>}

              <button
                type="submit"
                className="btn-primary"
                disabled={loading || code.length !== 6}
                data-testid="token-verify-btn"
              >
                {loading ? 'Verifying…' : 'Verify and continue'}
              </button>

              <button
                type="button"
                className="btn-link"
                onClick={handleRequestEmailToken}
                disabled={loading}
              >
                Resend code
              </button>
            </form>
          )}
        </main>
        <footer className="admin-login-foot">
          © {new Date().getFullYear()} CloudRadial
          {buildVersion && (
            <>
              {' · '}
              <span className="admin-login-version" data-testid="build-version">{buildVersion}</span>
            </>
          )}
        </footer>
      </div>

      <NotificationGroup
        style={{ position: 'fixed', right: 16, bottom: 16, alignItems: 'flex-end' }}
      >
        <Fade>
          {toast && (
            <Notification
              type={{ style: 'info', icon: true }}
              closable
              onClose={() => setToast(null)}
            >
              <span>{toast}</span>
            </Notification>
          )}
        </Fade>
      </NotificationGroup>
    </div>
  );
}

export default Login;
